import type { ReactNode, Ref } from "react";
import { useTranslation } from "react-i18next";
import { Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SelectionLiveRegion } from "@/components/ui/selection-live-region";
import { cn } from "@/lib/utils";

export type SelectionToolbarLayoutProps = {
  selectedCount: number;
  onClose: () => void;
  onSelectAll: () => void;
  selectAllDisabled?: boolean;
  /** Left side after “Vybrať všetko” (Pipeline: vlastník + ďalšie; Manuály: hromadná úprava; …) */
  bulkActions?: ReactNode;
  /** Right side before destructive delete (Export, Presunúť, …) */
  rightActions?: ReactNode;
  onDelete: () => void;
  deleteDisabled?: boolean;
  deletePending?: boolean;
  className?: string;
  closeButtonRef?: Ref<HTMLButtonElement>;
};

/**
 * Egységes kijelölés-toolbar (desktop / md+). A mobil nézetet a hívó oldal kezeli külön.
 */
export function SelectionToolbarLayout({
  selectedCount,
  onClose,
  onSelectAll,
  selectAllDisabled,
  bulkActions,
  rightActions,
  onDelete,
  deleteDisabled,
  deletePending,
  className,
  closeButtonRef,
}: SelectionToolbarLayoutProps) {
  const { t } = useTranslation();
  const delBlocked = Boolean(deleteDisabled || deletePending);

  return (
    <div
      className={cn(
        "flex h-full w-full min-w-0 items-center justify-between gap-3 px-3 sm:px-4",
        className,
      )}
    >
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2 md:gap-3">
        <Button
          ref={closeButtonRef}
          type="button"
          variant="outline"
          size="sm"
          className="h-9 w-9 shrink-0 p-0"
          aria-label={t("crm.clearSelection")}
          onClick={onClose}
        >
          <X className="h-4 w-4" />
        </Button>
        <SelectionLiveRegion count={selectedCount} />
        <span className="text-sm font-bold tabular-nums">
          {t("images.selectedCount", { count: selectedCount })}
        </span>
        <span className="text-sm text-muted-foreground">{t("common.selectItemsHint")}</span>
        <button
          type="button"
          className={cn(
            "shrink-0 text-sm font-medium text-primary no-underline hover:no-underline",
            "disabled:pointer-events-none disabled:opacity-50",
          )}
          disabled={selectAllDisabled}
          onClick={onSelectAll}
        >
          {t("common.selectAll")}
        </button>
        {bulkActions}
      </div>
      <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
        {rightActions}
        <Button
          type="button"
          variant="destructive"
          size="sm"
          className={cn("shrink-0 gap-1", delBlocked && "opacity-50")}
          disabled={delBlocked}
          onClick={onDelete}
        >
          <Trash2 className="h-4 w-4" />
          {t("common.delete")}
        </Button>
      </div>
    </div>
  );
}
